import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { CheckoutConfiguration } from '../models/commerce.models';

export interface PayPalButtons {
    render(element: HTMLElement): Promise<void>; close(): Promise<void>; isEligible(): boolean;
}
export interface PayPalSdk {
    CardFields?(options: { createOrder: () => Promise<string>; onApprove: (data: { orderID: string }) => Promise<void>;
        onError: (error: any) => void; style: any }): PayPalCardFields;
    Buttons(options: { fundingSource?: 'paypal' | 'card'; createOrder: () => Promise<string>; onApprove: (data: { orderID: string }) => Promise<void>;
        onCancel: () => void; onError: (error: any) => void; style: { layout: string } }): PayPalButtons;
}
export interface PayPalCardField { render(element: HTMLElement): Promise<void>; close(): Promise<void>; }
export interface PayPalCardFields {
    isEligible(): boolean;
    NameField(options?: any): PayPalCardField; NumberField(options?: any): PayPalCardField;
    ExpiryField(options?: any): PayPalCardField; CVVField(options?: any): PayPalCardField;
    submit(options?: any): Promise<void>;
}

/** Script lifecycle only; business data and payment state remain server-owned. */
@Injectable({ providedIn: 'root' })
export class PayPalSdkService {
    private loading: Promise<PayPalSdk>;
    private configurationKey: string;
    constructor(@Inject(DOCUMENT) private document: Document) {}
    load(config: CheckoutConfiguration): Promise<PayPalSdk> {
        const configurationErrors = [];
        if (!config?.payPalClientId) { configurationErrors.push('Client ID is missing'); }
        if (!['Sandbox', 'Live'].includes(config?.payPalEnvironment)) { configurationErrors.push('Environment must be Sandbox or Live'); }
        if (config?.currency !== 'USD') { configurationErrors.push('Currency must be USD'); }
        if (configurationErrors.length) {
            return Promise.reject(new Error('PayPal is not configured for this checkout: ' + configurationErrors.join('; ') + '.'));
        }
        const key = JSON.stringify([config.payPalClientId, config.payPalMerchantId, config.payPalEnvironment, config.currency]);
        if (this.loading) {
            return this.configurationKey === key ? this.loading :
                Promise.reject(new Error('Payment configuration changed. Reload this page.'));
        }
        this.configurationKey = key;
        this.loading = new Promise<PayPalSdk>((resolve, reject) => {
            const script = this.document.createElement('script');
            script.src = 'https://www.paypal.com/sdk/js?client-id=' + encodeURIComponent(config.payPalClientId) +
                '&currency=USD&intent=capture&components=buttons,card-fields' +
                (config.payPalMerchantId ? '&merchant-id=' + encodeURIComponent(config.payPalMerchantId) : '');
            script.setAttribute('data-namespace', 'fitnessPayPal');
            script.async = true;
            const timer = setTimeout(() => fail(), 30000);
            const fail = () => { clearTimeout(timer); script.remove(); this.loading = null;
                reject(new Error('PayPal could not load. Retry when your connection is available.')); };
            script.onerror = fail;
            script.onload = () => {
                clearTimeout(timer);
                const sdk = (this.document.defaultView as any).fitnessPayPal;
                if (!sdk || !sdk.Buttons) { fail(); return; } resolve(sdk);
            };
            this.document.head.appendChild(script);
        });
        return this.loading;
    }
}
